import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import * as utils from "../utils/utils";
import { useState, useEffect, useMemo, useRef } from "react";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Calendar } from "../components/common/Calendar";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import { Textarea } from "../components/common/Textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";

import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { supabase } from "../lib/supabase";
import config from "../config";

import { getLocalizedDayName } from "../i18n/scheduleLabels";
import {
  generateTimeSlots,
  formatDate,
  formatDateDMY,
  formatClockTimeRange,
  getActiveSchedulesForDate,
  getScheduleConsultationMode,
  resolveAppointmentConsultationMode,
  isSlotInPast,
} from "../lib/ScheduleUtils";
import {
  ConsultationModeBadge,
  ConsultationModePicker,
} from "./ConsultationMode";

import {
  CalendarDays,
  Clock,
  Phone,
  User,
  ArrowLeft,
  CheckCircle,
  Users,
  Search,
  Mail,
  Loader2,
  X,
  Stethoscope,
  AlertTriangle,
} from "lucide-react";

import customToast from "./common/Toaster";
import { isBookableDoctor } from "../utils/utils";
import useBrandTranslation from "../hooks/useBrandTranslation";

/** Fecha inicial si la URL trae ?date= (evita un frame con el día equivocado). */
function readDateFromSearchOnce() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const dateParam = params.get("date");
    if (!dateParam) return null;
    const d = DateTime.fromISO(`${dateParam}T00:00:00`, {
      zone: "America/Argentina/Buenos_Aires",
    });
    return d.isValid ? d.toJSDate() : null;
  } catch {
    return null;
  }
}

// Códigos de país para teléfono (bandera + código)
const PHONE_COUNTRY_CODES = [
  { code: "+54", label: "🇦🇷 +54", country: "Argentina" },
  { code: "+34", label: "🇪🇸 +34", country: "España" },
  { code: "+52", label: "🇲🇽 +52", country: "México" },
  { code: "+57", label: "🇨🇴 +57", country: "Colombia" },
  { code: "+56", label: "🇨🇱 +56", country: "Chile" },
  { code: "+598", label: "🇺🇾 +598", country: "Uruguay" },
  { code: "+51", label: "🇵🇪 +51", country: "Perú" },
  { code: "+593", label: "🇪🇨 +593", country: "Ecuador" },
  { code: "+591", label: "🇧🇴 +591", country: "Bolivia" },
  { code: "+595", label: "🇵🇾 +595", country: "Paraguay" },
  { code: "+55", label: "🇧🇷 +55", country: "Brasil" },
  { code: "+1", label: "🇺🇸 +1", country: "Estados Unidos" },
  { code: "+49", label: "🇩🇪 +49", country: "Alemania" },
  { code: "+33", label: "🇫🇷 +33", country: "Francia" },
  { code: "+39", label: "🇮🇹 +39", country: "Italia" },
  { code: "+44", label: "🇬🇧 +44", country: "Reino Unido" },
  { code: "+58", label: "🇻🇪 +58", country: "Venezuela" },
  { code: "+503", label: "🇸🇻 +503", country: "El Salvador" },
  { code: "+502", label: "🇬🇹 +502", country: "Guatemala" },
  { code: "+506", label: "🇨🇷 +506", country: "Costa Rica" },
  { code: "+507", label: "🇵🇦 +507", country: "Panamá" },
  { code: "+53", label: "🇨🇺 +53", country: "Cuba" },
  { code: "+972", label: "🇮🇱 +972", country: "Israel" },
  { code: "+86", label: "🇨🇳 +86", country: "China" },
  { code: "+81", label: "🇯🇵 +81", country: "Japón" },
  { code: "+91", label: "🇮🇳 +91", country: "India" },
];

// Parsea un teléfono guardado (ej. "+54 11 1234-5678") en código y número local
function parseStoredPhone(phoneStr) {
  if (!phoneStr || typeof phoneStr !== "string") return { code: "+54", local: "" };
  const trimmed = phoneStr.trim();
  if (trimmed.startsWith("+")) {
    const match = trimmed.match(/^(\+\d{1,4})\s*(.*)$/);
    if (match) {
      const code = match[1];
      const local = (match[2] || "").trim();
      const known = PHONE_COUNTRY_CODES.find((c) => c.code === code);
      return { code: known ? code : "+54", local };
    }
  }
  return { code: "+54", local: trimmed };
}

export default function SecretaryAssignAppointments() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const {
    profiles,
    schedules,
    appointments,
    secretaries,
    sharedSchedules,
    addAppointment,
    getSharedSchedulesBySecretary,
    loadData,
  } = useData();

  usePeriodicLoadData(
    loadData,
    undefined,
    ["secretary", "institution", "doctor"].includes(user?.role)
  );

  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(
    () => readDateFromSearchOnce() ?? new Date()
  );
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [patientPhone, setPatientPhone] = useState("");
  const [patientPhoneCountryCode, setPatientPhoneCountryCode] = useState("+54");
  const [patientEmail, setPatientEmail] = useState("");
  const [notes, setNotes] = useState("");
  // Evita reservas duplicadas por doble click / red lenta: el ref corta en el mismo tick,
  // el estado deshabilita el botón hasta tener la respuesta.
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modalidad elegida cuando el horario es "ambas" (presencial / virtual)
  const [chosenConsultationMode, setChosenConsultationMode] = useState(null);

  useEffect(() => {
    setChosenConsultationMode(null);
  }, [selectedSlot]);

  const consultationMode = resolveAppointmentConsultationMode(
    selectedSlot?.consultationMode,
    chosenConsultationMode
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDoctorId = searchParams.get("doctorId");
  
  // Estados para búsqueda de pacientes
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [patientFound, setPatientFound] = useState(null);
  const [patientResults, setPatientResults] = useState([]);
  const [showInviteOption, setShowInviteOption] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [cancellations, setCancellations] = useState([]);

  useEffect(() => {
    const loadCancellations = async () => {
      const { data, error } = await supabase
        .from("appointment_cancellations")
        .select("*");
      if (!error) setCancellations(data || []);
    };
    loadCancellations();
  }, []);

  const patientCancellationsCount = useMemo(() => {
    if (!patientFound || !cancellations?.length) return 0;
    return cancellations.filter((c) => {
      if (patientFound.id && c.patient_id) return c.patient_id === patientFound.id;
      const fullName = [patientFound.name, patientFound.last_name].filter(Boolean).join(" ").toLowerCase();
      if (c.patient_name && fullName) return c.patient_name.toLowerCase().trim() === fullName.trim();
      return false;
    }).length;
  }, [patientFound, cancellations]);

  const getSecretaryName = (secretaryId) => {
    if (secretaryId == null) return "Médico";
    const secretary = secretaries.find((s) => s.id === secretaryId);
    return secretary ? utils.getStaffDisplayName(secretary) : "Secretaria";
  };

  // Médico: usa sus propios horarios. Secretaria/institución: horarios compartidos.
  const mySharedSchedules = useMemo(() => {
    if (user?.role === "doctor") {
      return schedules
        .filter((s) => s.doctor_id === user.id)
        .map((s) => ({ schedule_id: s.id, secretary_id: null }));
    }
    if (user?.role === "secretary" || user?.role === "institution") {
      return getSharedSchedulesBySecretary(user.id).filter((shared) => {
        if (!selectedDoctorId) return true;
        const schedule = schedules.find((s) => s.id === shared.schedule_id);
        return schedule?.doctor_id === selectedDoctorId;
      });
    }
    return [];
  }, [user?.role, user?.id, schedules, selectedDoctorId, getSharedSchedulesBySecretary]);

  // Obtener los horarios completos
  const availableSchedules = useMemo(() => {
    return mySharedSchedules
      .map((shared) => schedules.find((s) => s.id === shared.schedule_id))
      .filter(Boolean)
      .filter((schedule) => {
        const doctor = profiles.find((p) => p.id === schedule.doctor_id);
        return isBookableDoctor(doctor);
      });
  }, [mySharedSchedules, schedules, profiles]);

  // Hora actual (se refresca cada minuto) para ofrecer, en el mismo día,
  // solo los horarios desde el siguiente slot a la hora actual.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Obtener todos los slots para la fecha seleccionada (disponibles + ocupados)
  const allSlots = useMemo(() => {
    const effectiveDateStr = formatDate(selectedDate);

    const daySchedules = availableSchedules.filter(
      (s) =>
        s &&
        getActiveSchedulesForDate([s], s.doctor_id, selectedDate).length > 0
    );
    if (daySchedules.length === 0) return [];

    const slots = daySchedules.flatMap((schedule) => {
      if (!schedule) return [];
      const doctorAppointments = appointments.filter((a) => {
        const appointmentDate = (a.date || "").substring(0, 10);
        return (
          appointmentDate === effectiveDateStr &&
          String(a.doctor_id) === String(schedule.doctor_id)
        );
      });
      const generated = generateTimeSlots(
        schedule.start_time,
        schedule.end_time,
        schedule.slot_duration,
        schedule.appointment_type,
        doctorAppointments,
        getScheduleConsultationMode(schedule)
      );
      return generated.map((slot) => ({
        ...slot,
        doctorId: schedule.doctor_id,
        scheduleId: schedule.id,
      }));
    });

    return slots
      .filter((slot) => !isSlotInPast(selectedDate, slot.startTime, now))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [selectedDate, availableSchedules, appointments, now]);

  // Si el horario elegido quedó en el pasado mientras se completaba el formulario, se descarta.
  useEffect(() => {
    if (selectedSlot && isSlotInPast(selectedDate, selectedSlot.startTime, now)) {
      setSelectedSlot(null);
    }
  }, [now, selectedDate, selectedSlot]);

  // Manejar parámetros de URL para navegación directa - Solo una vez al montar

  useEffect(() => {
    const dateParam = searchParams.get("date");
    const timeParam = searchParams.get("time");

    if (dateParam) {
      // Opción 1: Crear la fecha directamente en la zona horaria de Buenos Aires
      const selectedDate = DateTime.fromISO(`${dateParam}T00:00:00`, {
        zone: "America/Argentina/Buenos_Aires",
      });

      if (selectedDate.isValid) {
        setSelectedDate(selectedDate.toJSDate());
      }
    }
  }, [searchParams]);

  // Auto-selección desde ?time= y opcionalmente ?doctorId=; si hay varios médicos a la misma hora, prioriza el ocupado (sobreturno)
  useEffect(() => {
    const timeParam = searchParams.get("time");
    const doctorParam = searchParams.get("doctorId");
    if (!timeParam || allSlots.length === 0 || selectedSlot) return;

    const matches = allSlots.filter((slot) => {
      if (slot.startTime !== timeParam) return false;
      if (doctorParam && String(slot.doctorId) !== String(doctorParam)) return false;
      return true;
    });

    if (matches.length === 0) return;

    const preferOccupied = matches.find((s) => !s.isAvailable) ?? matches[0];
    setSelectedSlot(preferOccupied);
  }, [allSlots, searchParams, selectedSlot]);

  const getDoctorName2 = (doctorId) => {
    return doctorId === "550e8400-e29b-41d4-a716-446655440001"
      ? "Dr. Juan Pérez"
      : "Médico";
  };

  const getDoctorName = (doctorId) => {
    if (!doctorId) return "Médico";
    const profile = profiles.find((p) => p.id === doctorId);
    if (profile) return profile.name ? `Dr. ${profile.name} ${profile.last_name || ""}`.trim() : "Médico";
    const schedule = schedules.find((s) => s.doctor_id === doctorId);
    if (!schedule) return "Médico";
    const p = profiles.find((x) => x.id === schedule.doctor_id);
    return p ? (p.name || "Médico") : "Médico";
  };

  const getDoctorId = () => {
    if (user?.role === "doctor") return user.id;
    if (selectedDoctorId) return selectedDoctorId;
    if (selectedSlot?.doctorId) return selectedSlot.doctorId;

    const firstShared = mySharedSchedules[0];
    if (firstShared) {
      const schedule = schedules.find((s) => s.id === firstShared.schedule_id);
      return schedule?.doctor_id || null;
    }
    return null;
  };

  const getDoctorId2 = () => {
    // Obtener el ID del médico del primer horario compartido
    const firstShared = mySharedSchedules[0];
    if (firstShared) {
      const schedule = schedules.find((s) => s.id === firstShared.schedule_id);
      return schedule?.doctor_id || "550e8400-e29b-41d4-a716-446655440001";
    }
    return "550e8400-e29b-41d4-a716-446655440001";
  };

  // Función para seleccionar un paciente de los resultados
  const selectPatient = (patient) => {
    setPatientFound(patient);
    setPatientResults([]);
    setPatientName(`${patient.name || ""} ${patient.last_name || ""}`.trim());
    const { code, local } = parseStoredPhone(patient.phone || "");
    setPatientPhoneCountryCode(code);
    setPatientPhone(local);
    setPatientEmail(patient.email || "");
    setShowInviteOption(false);
  };

  // Función para buscar pacientes por email o parte del email
  const searchPatient = async () => {
    if (!searchTerm.trim()) {
      customToast(t("app.appointments.searchEmail", "Por favor ingresa un email para buscar"), "warning");
      return;
    }

    setSearching(true);
    setPatientFound(null);
    setPatientResults([]);
    setShowInviteOption(false);

    try {
      const searchValue = searchTerm.trim();
      
      // Buscar por email (búsqueda parcial)
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("role", "patient")
        .is("deleted_at", null)
        .ilike("email", `%${searchValue}%`)
        .limit(10);

      if (error) throw error;

      if (data && data.length > 0) {
        if (data.length === 1) {
          // Si hay un solo resultado, seleccionarlo automáticamente
          selectPatient(data[0]);
        } else {
          // Si hay múltiples resultados, mostrarlos para seleccionar
          setPatientResults(data);
        }
      } else {
        setPatientFound(null);
        setPatientResults([]);
        // Si el término de búsqueda parece un email, usarlo directamente
        if (searchValue.includes("@")) {
          setPatientEmail(searchValue);
        }
        setShowInviteOption(true);
      }
    } catch (error) {
      console.error("Error buscando paciente:", error);
      customToast(t("app.appointments.searchError", "Error al buscar el paciente. Intente nuevamente."), "danger");
    } finally {
      setSearching(false);
    }
  };

  // Función para enviar invitación por email
  const sendInvitation = async () => {
    const emailToSend = patientEmail.trim() || searchTerm.trim();
    
    if (!emailToSend) {
      customToast(t("app.appointments.inviteEmail", "Por favor ingresa un email para enviar la invitación"), "warning");
      return;
    }

    if (!emailToSend.includes("@")) {
      customToast(t("app.appointments.inviteInvalidEmail", "Por favor ingresa un email válido"), "warning");
      return;
    }

    setSendingInvite(true);

    try {
      const response = await fetch(`${config.baseUrlApi}/utils/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: searchTerm.trim().split("@")[0] || "Usuario",
          emailTo: emailToSend,
        }),
      });

      const result = await response.json();

      if (response.ok && !result.error) {
        customToast(t("app.appointments.inviteSuccess", "Invitación enviada exitosamente por email"), "success");
        setShowInviteOption(false);
        // Usar el email como nombre si no hay otro nombre
        if (!patientName.trim()) {
          setPatientName(emailToSend.split("@")[0]);
        }
        setPatientEmail(emailToSend);
      } else {
        throw new Error(result.error || "Error al enviar la invitación");
      }
    } catch (error) {
      console.error("Error enviando invitación:", error);
      customToast(t("app.appointments.inviteError", "Error al enviar la invitación. Intente nuevamente."), "danger");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleAssignAppointment = async () => {
    if (!selectedSlot || !patientName.trim() || !notes.trim()) {
      customToast(t("app.appointments.fillRequired", "Por favor completa todos los campos requeridos (incluido el motivo)"), "warning");
      return;
    }
    if (!consultationMode) {
      customToast(t("schedule.consultationModeRequired", "Elegí si el turno es presencial o virtual"), "warning");
      return;
    }

    const doctorId = getDoctorId();
    if (!doctorId) {
      customToast(t("app.appointments.noSlots", "No hay horarios disponibles para asignar."), "warning");
      return;
    }

    const fullPhone =
      patientPhone.trim() !== ""
        ? `${patientPhoneCountryCode} ${patientPhone.trim()}`
        : undefined;

    const isOverbook = !selectedSlot.isAvailable;

    const appointment = {
      doctorId,
      patientId: patientFound?.id || undefined,
      secretaryId: user.role === "doctor" ? undefined : user.id,
      date: selectedDate,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      appointmentType: selectedSlot.appointmentType,
      consultationMode,
      status: "booked",
      patientName: patientName.trim(),
      patientPhone: fullPhone,
      notes: notes.trim() || undefined,
      price: undefined,
      isOverbook,
      assignedByStaff: true,
    };

    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const result = await addAppointment(appointment);

      if (result?.ok && result.appointment) {
        customToast(
          isOverbook
            ? `Sobreturno asignado exitosamente para ${patientName.trim()}`
            : `Turno asignado exitosamente para ${patientName.trim()}`,
          "success"
        );
        // Recargar datos para actualizar todas las vistas
        await loadData();
        // Limpiar formulario
        setSelectedSlot(null);
        setPatientName("");
        setPatientPhone("");
        setPatientPhoneCountryCode("+54");
        setPatientEmail("");
        setNotes("");
        setSearchTerm("");
        setPatientFound(null);
        setShowInviteOption(false);
      } else if (!result?.userNotified) {
        customToast(t("app.appointments.assignError", "Error al asignar el turno. Intente nuevamente."), "danger");
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Turnos asignados por mí: secretaria por secretary_id; médico por doctor_id
  const myAssignedAppointments = useMemo(() => {
    if (!user?.id) return [];

    const filtered =
      user.role === "doctor"
        ? appointments.filter((apt) => apt.doctor_id === user.id)
        : appointments.filter((apt) => apt.secretary_id === user.id);

    return filtered.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [appointments, user?.id, user?.role]);

  const allAssignedAppointments = useMemo(() => {
    if (!user?.id) return [];

    const list =
      user.role === "doctor"
        ? appointments.filter((apt) => apt.doctor_id === user.id)
        : appointments;

    return list.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [appointments, user?.id, user?.role]);

  // Obtener próximos turnos asignados (próximos 7 días)
  const upcomingAssignedAppointments = useMemo(() => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    return myAssignedAppointments
      .filter((apt) => {
        const aptDate = new Date(apt.date);
        return (
          aptDate >= today && aptDate <= nextWeek && apt.status === "booked"
        );
      })
      .slice(0, 3); // Solo los próximos 3
  }, [myAssignedAppointments]);

  // Estadísticas
  const totalAssigned = myAssignedAppointments.filter(
    (apt) => apt.status === "booked"
  ).length;
  const todayAssigned = myAssignedAppointments.filter((apt) => {
    const today = formatDate(new Date());
    const aptDateStr = (apt.date || "").substring(0, 10);
    return aptDateStr === today && apt.status === "booked";
  }).length;

  const canAssignAppointments = user && ["doctor", "secretary", "institution"].includes(user.role);
  if (!user) return null;
  if (!canAssignAppointments) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.appointments.restrictedAccessLower", "Acceso restringido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              {t(
                "app.appointments.assignRolesOnly",
                "Solo médicos, secretarias e instituciones pueden asignar turnos."
              )}
            </p>
            <Button variant="outline" className="mt-4" onClick={() => utils.goBack()}>
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Asignar Turnos
          </h1>
          {selectedDoctorId && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200 mb-2">
              <p className="text-blue-800 font-medium">
                Asignando turnos para: {getDoctorName(selectedDoctorId)}
              </p>
            </div>
          )}
          <p className="text-gray-600 ml-1">
            {user?.role === "doctor"
              ? "Asigna turnos a pacientes en tu agenda"
              : "Asigna turnos a pacientes en los horarios compartidos contigo"}
          </p>
        </div>

        {/* Resumen de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{totalAssigned}</p>
                  <p className="text-sm text-gray-600">{t("app.secretary.totalAssigned", "Total Asignados")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-green-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{todayAssigned}</p>
                  <p className="text-sm text-gray-600">{t("app.appointments.today", "Hoy")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {mySharedSchedules.length}
                  </p>
                  <p className="text-sm text-gray-600">
                    {user?.role === "doctor" ? "Mis Horarios" : "Horarios Compartidos"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {upcomingAssignedAppointments.length}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.appointments.next7Days", "Próximos 7 días")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendario */}
          <Card>
            <CardHeader>
              <CardTitle>{t("app.appointments.selectDate", "Seleccionar Fecha")}</CardTitle>
            </CardHeader>
            <CardContent className="!p-3">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(date);
                    setSelectedSlot(null); // Limpiar slot seleccionado al cambiar fecha
                  }
                }}
                disabled={(date) => {
                  const today = new Date();
                  const normalizeDate = (d) =>
                    new Date(d.getFullYear(), d.getMonth(), d.getDate());
                  return normalizeDate(date) < normalizeDate(today);
                }}
                className="rounded-md border"
              />
            </CardContent>
          </Card>

          {/* Horarios disponibles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {getLocalizedDayName(selectedDate.getDay())}{" "}
                {formatDateDMY(selectedDate)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {allSlots.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {formatDate(selectedDate) === formatDate(now)
                    ? t("app.appointments.noSlotsLeftToday", "No quedan horarios para hoy")
                    : t("app.appointments.noSlotsForDate", "No hay horarios para esta fecha")}
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {allSlots.map((slot, index) => {
                    const isSelected =
                      selectedSlot &&
                      selectedSlot.startTime === slot.startTime &&
                      selectedSlot.endTime === slot.endTime &&
                      selectedSlot.isAvailable === slot.isAvailable &&
                      String(selectedSlot.doctorId ?? "") === String(slot.doctorId ?? "") &&
                      String(selectedSlot.scheduleId ?? "") === String(slot.scheduleId ?? "");
                    const isOccupied = !slot.isAvailable;
                    const showDoctorLabel =
                      ["secretary", "institution"].includes(user?.role) &&
                      !selectedDoctorId &&
                      slot.doctorId;

                    return (
                      <Button
                        key={`${slot.scheduleId ?? "s"}-${slot.doctorId ?? "d"}-${slot.startTime}-${slot.endTime}-${slot.isAvailable}-${index}`}
                        variant={isSelected ? "default" : "outline"}
                        className="w-full h-auto p-3"
                        style={
                          isSelected && !isOccupied
                            ? { backgroundColor: "#dcfce7", color: "#166534", borderColor: "#86efac", border: "1px solid #86efac" }
                            : isSelected && isOccupied
                            ? { backgroundColor: "#ffedd5", color: "#9a3412", borderColor: "#fdba74", border: "1px solid #fdba74" }
                            : isOccupied
                            ? { backgroundColor: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca", border: "1px solid #fecaca" }
                            : {}
                        }
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <div className="flex items-center justify-between w-full gap-2">
                          <div className="flex flex-col items-start gap-0.5 min-w-0">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 shrink-0" />
                              <span>
                                {formatClockTimeRange(slot.startTime, slot.endTime)}
                              </span>
                            </div>
                            {showDoctorLabel && (
                              <span className="text-xs text-gray-600 pl-6 truncate max-w-full">
                                {getDoctorName(slot.doctorId)}
                              </span>
                            )}
                          </div>
                          {!isOccupied && (
                            <Badge
                              variant={
                                slot.appointmentType === "private"
                                  ? "appointmentPrivate"
                                  : "appointmentInsurance"
                              }
                              className="shrink-0"
                            >
                              {slot.appointmentType === "private"
                                ? "Particular"
                                : "Obra Social"}
                            </Badge>
                          )}
                          {!isOccupied && (
                            <ConsultationModeBadge
                              mode={slot.consultationMode}
                              className="shrink-0"
                            />
                          )}
                        </div>
                        {isOccupied && (
                          <div className="flex flex-col items-center gap-2 w-full mt-1">
                            <Badge
                            variant={
                              slot.appointmentType === "private"
                                ? "appointmentPrivate"
                                : "appointmentInsurance"
                            }
                          >
                            {slot.appointmentType === "private"
                              ? "Particular"
                              : "Obra Social"}
                          </Badge>
                            <Badge variant="sobreturno" className="text-xs">
                              Crear Sobreturno
                            </Badge>
                          </div>
                        )}
                      </Button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Formulario de asignación */}
          <Card>
            <CardHeader>
              <CardTitle>{t("app.appointments.patientData", "Datos del Paciente")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Datos del profesional al que se asigna el turno */}
              {getDoctorId() && (() => {
                const doctorId = getDoctorId();
                const doctorProfile = profiles.find((p) => p.id === doctorId);
                if (!doctorProfile) return null;
                return (
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">
                      Profesional
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">
                          {doctorProfile.name} {doctorProfile.last_name || ""}
                        </span>
                      </div>
                      {doctorProfile.specialization && (
                        <div className="flex items-center gap-2">
                          <Stethoscope className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">{doctorProfile.specialization}</span>
                        </div>
                      )}
                      {doctorProfile.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">{doctorProfile.phone}</span>
                        </div>
                      )}
                      {doctorProfile.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">{doctorProfile.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {selectedSlot && (
                <div className={`p-4 rounded-lg mb-4 ${!selectedSlot.isAvailable ? "bg-orange-50 border border-orange-200" : "bg-blue-50"}`}>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    {!selectedSlot.isAvailable && <AlertTriangle className="w-4 h-4 text-orange-600" />}
                    {!selectedSlot.isAvailable ? "Sobreturno:" : "Turno Seleccionado:"}
                  </h4>
                  {!selectedSlot.isAvailable && (
                    <p className="text-xs text-orange-700 mb-2">
                      Este horario ya tiene un turno asignado. Se creará un sobreturno.
                    </p>
                  )}
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      <span>
                        {formatDateDMY(selectedDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>
                        {formatClockTimeRange(selectedSlot.startTime, selectedSlot.endTime)}
                      </span>
                    </div>
                    <Badge
                      variant={
                        selectedSlot.appointmentType === "private"
                          ? "appointmentPrivate"
                          : "appointmentInsurance"
                      }
                    >
                      {selectedSlot.appointmentType === "private"
                        ? "Particular"
                        : "Obra Social"}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Búsqueda de paciente */}
              <div>
                <Label htmlFor="searchPatient">{t("app.secretary.searchPatientEmail", "Buscar Paciente por Email")}</Label>
                <div className="flex gap-2">
                  <Input
                    id="searchPatient"
                    type="email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        searchPatient();
                      }
                    }}
                    placeholder={t("app.secretary.searchEmailPlaceholder", "Ingresa email o parte del email")}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={searchPatient}
                    disabled={searching || !searchTerm.trim()}
                    variant="outline"
                    className="mt-3"
                  >
                    {searching ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {patientResults.length > 0 && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium mb-2">
                      Se encontraron {patientResults.length} paciente(s). Selecciona uno:
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {patientResults.map((patient) => (
                        <button
                          key={patient.id}
                          type="button"
                          onClick={() => selectPatient(patient)}
                          className="w-full text-left p-2 bg-white border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {patient.name} {patient.last_name}
                              </p>
                              <p className="text-xs text-gray-600">
                                {patient.email}
                                {patient.phone && ` • ${patient.phone}`}
                              </p>
                            </div>
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {patientFound && patientResults.length === 0 && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium mb-1">
                      ✓ Paciente seleccionado
                    </p>
                    <p className="text-sm text-green-700">
                      {patientFound.name} {patientFound.last_name}
                      {patientFound.email && ` - ${patientFound.email}`}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Cancelaciones en historial: {patientCancellationsCount}
                    </p>
                  </div>
                )}
                {showInviteOption && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg relative">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteOption(false);
                        setPatientEmail("");
                      }}
                      className="absolute top-2 right-2 text-yellow-600 hover:text-yellow-800 transition-colors"
                      aria-label={t("common.close", "Cerrar")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-sm text-yellow-800 font-medium mb-2 pr-6">
                      Paciente no encontrado
                    </p>
                    <p className="text-sm text-yellow-700 mb-3">
                      El paciente no está registrado en el sistema. Puedes enviarle una invitación por email para que se registre.
                    </p>
                    <div className="space-y-2">
                      <Input
                        type="email"
                        value={patientEmail}
                        onChange={(e) => setPatientEmail(e.target.value)}
                        placeholder={t("app.secretary.patientEmailPlaceholder", "Email del paciente")}
                      />
                      <Button
                        type="button"
                        onClick={sendInvitation}
                        disabled={sendingInvite || !patientEmail.trim()}
                        variant="outline"
                        className="w-full"
                      >
                        {sendingInvite ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Enviar Invitación por Email
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="patientName">{t("app.secretary.patientName", "Nombre del Paciente *")}</Label>
                <Input
                  id="patientName"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  placeholder={t("app.secretary.patientNamePlaceholder", "Nombre completo del paciente")}
                />
              </div>

              {selectedSlot && (
                <ConsultationModePicker
                  slotMode={selectedSlot.consultationMode}
                  value={chosenConsultationMode}
                  onChange={setChosenConsultationMode}
                />
              )}

              <div>
                <Label htmlFor="patientPhone">{t("app.booking.phone", "Teléfono")}</Label>
                <div className="flex gap-2">
                  <Select
                    value={patientPhoneCountryCode}
                    onValueChange={(value) => setPatientPhoneCountryCode(value)}
                  >
                    <SelectTrigger className="w-[130px] shrink-0 h-[48px] mt-2">
                      <SelectValue placeholder={t("app.booking.countryCode", "Código")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PHONE_COUNTRY_CODES.map(({ code, label }) => (
                        <SelectItem key={code} value={code}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="patientPhone"
                    value={patientPhone}
                    onChange={(e) => setPatientPhone(e.target.value)}
                    placeholder={t("app.booking.phonePlaceholder", "Ej: 11 1234-5678")}
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">{t("app.booking.reason", "Motivo *")}</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("app.booking.reasonPlaceholder", "Motivo de consulta, observaciones, etc.")}
                  rows={3}
                />
              </div>

              <Button
                onClick={handleAssignAppointment}
                disabled={
                  !selectedSlot ||
                  !patientName.trim() ||
                  !notes.trim() ||
                  !consultationMode ||
                  isSubmitting
                }
                className={`w-full ${selectedSlot && !selectedSlot.isAvailable ? "!bg-orange-600 hover:!bg-orange-700" : ""}`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("app.appointments.assigning", "Asignando turno…")}
                  </>
                ) : selectedSlot && !selectedSlot.isAvailable ? (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Asignar Sobreturno a {patientName.trim() || "Paciente"}
                  </>
                ) : (
                  `Asignar Turno a ${patientName.trim() || "Paciente"}`
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Todos los turnos asignados por esta secretaria */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t("app.appointments.assignedHistory", "Historial de Turnos Asignados")}</CardTitle>
          </CardHeader>
          <CardContent>
            {allAssignedAppointments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No has asignado turnos aún
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allAssignedAppointments.slice(0, 9).map((appointment) => {
                  const assignedByLabel = t(
                    "app.appointments.assignedBySecretary",
                    "Asignado por {{name}}",
                    { name: getSecretaryName(appointment.secretary_id) }
                  );

                  return (
                  <div key={appointment.id} className="border rounded-lg p-4 min-w-0 overflow-hidden">
                    <div className="flex items-start justify-between gap-2 mb-2 min-w-0">
                      <Badge
                        variant="outline"
                        className="bg-blue-50 min-w-0 max-w-[calc(100%-5.75rem)] shrink overflow-hidden"
                        title={assignedByLabel}
                      >
                        <span className="block truncate">{assignedByLabel}</span>
                      </Badge>
                      <Badge variant="outline" className="text-xs shrink-0 whitespace-nowrap">
                        {appointment.status === "booked"
                          ? t("app.appointments.statusConfirmed", "Confirmado")
                          : appointment.status}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium">
                          {appointment.patient_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">
                          {formatDateDMY(appointment.date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">
                          {formatClockTimeRange(appointment.start_time, appointment.end_time)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            appointment.appointment_type === "private"
                              ? "appointmentPrivate"
                              : "appointmentInsurance"
                          }
                          className="text-xs"
                        >
                          {appointment.appointment_type === "private"
                            ? "Particular"
                            : "Obra Social"}
                        </Badge>
                        <ConsultationModeBadge
                          mode={appointment.consultation_mode}
                          className="text-xs"
                        />
                      </div>
                      {appointment.patient_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">
                            {appointment.patient_phone}
                          </span>
                        </div>
                      )}
                      {appointment.notes && (
                        <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                          <strong>{t("app.appointments.notes", "Notas:")}</strong> {appointment.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            {allAssignedAppointments.length > 9 && (
              <div className="text-center mt-4">
                <Button variant="outline">{t("app.appointments.viewMoreAppointments", "Ver Más Turnos")}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
